import { Download, DocumentImport } from '@carbon/icons-react';
import {
  Button,
  Dropdown,
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import {
  asProblem,
  hasPermission,
  ListTable,
  PageHeader,
  problemMessage,
  RequirePermission,
  useAdmin,
  useFormat,
  useLocalPages,
} from '@pos/admin-ui';
import { fold, useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ErrorNotice, TransientNotice } from '../../common/Notices';
import { brandsKey, listAllPages, scopeApis } from '../../delivery/deliveryApi';
import {
  applyImport,
  downloadWorkbook,
  IMPORT_MANAGE,
  IMPORT_READ,
  IMPORT_SHEETS,
  previewImport,
  type ImportReport,
} from './importApi';
import './import.scss';

/** The server refuses larger files (`import.file_invalid`); checked here too so nothing is sent for nothing. */
const MAX_BYTES = 10 * 1024 * 1024;

const n = (value: number | string | null | undefined) => Number(value ?? 0);

type Step = 'previewing' | 'importing' | undefined;

/**
 * Importing a brand's menu data from one Excel workbook: categories, items, units, options, menus, layouts, price lists,
 * prices, menu items and POS buttons. The workbook to fill is the brand's current data. Choosing a file shows what the import
 * would do, sheet by sheet, and every problem by sheet, row and column; nothing is written until the user imports, and then
 * in one transaction or not at all. Rows are matched by code and nothing is removed. Stores get the changes with the next
 * master data publish, as for any change.
 */
export function ImportPage() {
  const { session } = useAdmin();
  return (
    <RequirePermission session={session} permission={IMPORT_READ}>
      <ImportScreen canImport={hasPermission(session, IMPORT_MANAGE)} />
    </RequirePermission>
  );
}

function ImportScreen({ canImport }: { canImport: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const brands = useQuery({ queryKey: brandsKey, queryFn: () => listAllPages(scopeApis.brands) });
  const [brandId, setBrandId] = useState<string | undefined>(params.get('brandId') ?? undefined);
  const brandList = brands.data ?? [];
  const brand = brandList.find((b) => b.id === brandId)?.id ?? brandList[0]?.id;
  const [file, setFile] = useState<File>();
  const [fileError, setFileError] = useState<string>();
  const [report, setReport] = useState<ImportReport>();
  const [step, setStep] = useState<Step>();
  const [problem, setProblem] = useState<string>();
  const [notice, setNotice] = useState<string>();
  // Only the answer for the latest file and brand counts; an older preview finishing late is dropped.
  const latest = useRef(0);

  const sheetName = (sheet: string) => t(`menuImport.sheet.${sheet}` as I18nKey, { defaultValue: sheet });

  const preview = async (target: File, forBrand: string) => {
    const request = ++latest.current;
    setReport(undefined);
    setProblem(undefined);
    setStep('previewing');
    try {
      const result = await previewImport(forBrand, target);
      if (request === latest.current) setReport(result);
    } catch (error) {
      if (request === latest.current) setProblem(problemMessage(t, asProblem(error)));
    } finally {
      if (request === latest.current) setStep(undefined);
    }
  };

  const choose = (added: (File & { invalidFileType?: boolean })[]) => {
    const chosen = added[0];
    if (!chosen) return;
    setNotice(undefined);
    setFile(chosen);
    const invalid = chosen.invalidFileType
      ? t('menuImport.invalidType')
      : chosen.size > MAX_BYTES
        ? t('menuImport.tooLarge')
        : undefined;
    setFileError(invalid);
    if (invalid) {
      latest.current++;
      setReport(undefined);
      setStep(undefined);
      return;
    }
    if (brand) void preview(chosen, brand);
  };

  const clear = () => {
    latest.current++;
    setFile(undefined);
    setFileError(undefined);
    setReport(undefined);
    setProblem(undefined);
    setStep(undefined);
  };

  const download = useMutation({
    mutationFn: () => downloadWorkbook(brand ?? ''),
    onSuccess: ({ blob, fileName }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName ?? `menu-data-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: () => setProblem(t('menuImport.downloadFailed')),
  });

  const runImport = async () => {
    if (!file || !brand) return;
    const request = ++latest.current;
    setProblem(undefined);
    setStep('importing');
    try {
      const result = await applyImport(brand, file);
      if (request !== latest.current) return;
      if (result.applied) {
        const total = (key: 'created' | 'updated' | 'restored' | 'unchanged') =>
          format.number(result.sheets.reduce((sum, s) => sum + n(s[key]), 0));
        setNotice(
          t('menuImport.done', {
            created: total('created'),
            updated: total('updated'),
            restored: total('restored'),
            unchanged: total('unchanged'),
          }),
        );
        clear();
        await queryClient.invalidateQueries();
      } else {
        // Someone changed the data since the preview: the problems now stand in the way.
        setReport(result);
      }
    } catch (error) {
      if (request === latest.current) setProblem(problemMessage(t, asProblem(error)));
    } finally {
      if (request === latest.current) setStep(undefined);
    }
  };

  const problemCount = n(report?.problemCount);
  const sheetRows = (report?.sheets ?? [])
    .filter((s) => n(s.created) + n(s.updated) + n(s.restored) + n(s.unchanged) > 0)
    .sort((a, b) => sheetOrder(a.sheet) - sheetOrder(b.sheet))
    .map((s) => ({ ...s, id: s.sheet }));

  const [search, setSearch] = useState('');
  const needle = fold(search.trim());
  const problemRows = (report?.problems ?? [])
    .map((p, index) => ({ ...p, id: String(index), sheetLabel: sheetName(p.sheet) }))
    .filter(
      (p) => !needle || [p.sheetLabel, p.column ?? '', p.message, String(p.row)].some((v) => fold(v).includes(needle)),
    );
  const problemPages = useLocalPages(problemRows);

  const busy = step !== undefined;
  const fileStatus = step ? 'uploading' : fileError || problem || problemCount > 0 ? 'edit' : 'complete';

  return (
    <section className="pos-admin-page pos-menu-import">
      <PageHeader
        title={t('menuImport.title')}
        description={t('menuImport.intro')}
        actions={
          brand ? (
            <Button
              kind="tertiary"
              renderIcon={Download}
              disabled={download.isPending}
              onClick={() => download.mutate()}
            >
              {t('menuImport.download')}
            </Button>
          ) : undefined
        }
      />
      {brands.error && (
        <ErrorNotice title={problemMessage(t, asProblem(brands.error))} onRetry={() => void brands.refetch()} />
      )}
      {notice && <TransientNotice kind="success" title={notice} onDone={() => setNotice(undefined)} />}
      {problem && (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          title={problem}
          onClose={() => setProblem(undefined)}
        />
      )}

      <div className="pos-menu-import__choose">
        {brandList.length > 1 && (
          <Dropdown
            id={`${id}-brand`}
            titleText={t('menuImport.brand')}
            label={t('menuImport.chooseBrand')}
            items={brandList}
            itemToString={(b) => b?.name ?? ''}
            selectedItem={brandList.find((b) => b.id === brand) ?? null}
            disabled={busy}
            onChange={({ selectedItem }) => {
              if (!selectedItem) return;
              setBrandId(selectedItem.id);
              if (file && !fileError) void preview(file, selectedItem.id);
            }}
          />
        )}
        {canImport && brand && (
          <div className="pos-menu-import__file">
            <p className="pos-menu-import__hint">{t('menuImport.fileHint')}</p>
            <FileUploaderDropContainer
              id={`${id}-file`}
              labelText={t('menuImport.drop')}
              accept={['.xlsx']}
              multiple={false}
              disabled={busy}
              onAddFiles={(_event, { addedFiles }) => choose(addedFiles)}
            />
            {file && (
              <FileUploaderItem
                uuid={`${id}-chosen`}
                name={file.name}
                status={fileStatus}
                invalid={Boolean(fileError)}
                errorSubject={fileError}
                iconDescription={step ? t('menuImport.checking') : t('menuImport.remove', { name: file.name })}
                onDelete={clear}
              />
            )}
          </div>
        )}
      </div>

      {step === 'previewing' && <InlineLoading description={t('menuImport.checking')} />}

      {report && problemCount > 0 && (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={t('menuImport.problems', { count: problemCount })}
          subtitle={
            problemCount > report.problems.length
              ? t('menuImport.firstProblems', { count: report.problems.length })
              : undefined
          }
        />
      )}

      {report && sheetRows.length > 0 && (
        <ListTable
          title={t('menuImport.summary')}
          ariaLabel={t('menuImport.summary')}
          rows={sheetRows}
          columns={[
            { key: 'sheet', header: t('menuImport.columns.sheet'), render: (s) => sheetName(s.sheet) },
            {
              key: 'created',
              header: t('menuImport.columns.created'),
              numeric: true,
              render: (s) => format.number(n(s.created)),
            },
            {
              key: 'updated',
              header: t('menuImport.columns.updated'),
              numeric: true,
              render: (s) => format.number(n(s.updated)),
            },
            {
              key: 'restored',
              header: t('menuImport.columns.restored'),
              numeric: true,
              render: (s) => format.number(n(s.restored)),
            },
            {
              key: 'unchanged',
              header: t('menuImport.columns.unchanged'),
              numeric: true,
              render: (s) => format.number(n(s.unchanged)),
            },
          ]}
        />
      )}
      {report && sheetRows.length === 0 && problemCount === 0 && (
        <InlineNotification kind="info" lowContrast hideCloseButton title={t('menuImport.emptyFile')} />
      )}

      {report && report.problems.length > 0 && (
        <ListTable
          title={t('menuImport.problemsTitle')}
          ariaLabel={t('menuImport.problemsTitle')}
          search={{
            label: t('menuImport.searchProblems'),
            onChange: (value) => {
              setSearch(value);
              problemPages.reset();
            },
          }}
          rows={problemPages.rows}
          pagination={problemPages.pagination}
          empty={{ heading: t('menuImport.noMatch') }}
          columns={[
            { key: 'sheetLabel', header: t('menuImport.columns.sheet') },
            {
              key: 'row',
              header: t('menuImport.columns.row'),
              numeric: true,
              render: (p) => (n(p.row) > 0 ? format.number(n(p.row)) : ''),
            },
            { key: 'column', header: t('menuImport.columns.column'), render: (p) => p.column ?? '' },
            { key: 'message', header: t('menuImport.columns.message') },
          ]}
        />
      )}

      {canImport && brand && (
        <div className="pos-menu-import__actions">
          <Button
            renderIcon={DocumentImport}
            disabled={!report || problemCount > 0 || busy || Boolean(fileError)}
            onClick={() => void runImport()}
          >
            {t('menuImport.import')}
          </Button>
          {step === 'importing' && <InlineLoading description={t('menuImport.importing')} />}
        </div>
      )}
    </section>
  );
}

const sheetOrder = (sheet: string) => {
  const index = (IMPORT_SHEETS as readonly string[]).indexOf(sheet);
  return index < 0 ? IMPORT_SHEETS.length : index;
};
